import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronLeft, FileText, DollarSign, Image as ImageIcon, Layers, Download, Calendar, Calculator as CalcIcon, Boxes } from 'lucide-react';
import { fetchProducts, createProduct, updateProduct } from './productSlice';
import { useToast } from '../../components/ui/Toast';
import Shell from '../dashboard/Shell';
import ProductForm from './ProductForm';
import CalculatorModal from '../utilities/calculator/CalculatorModal';
import './ProductFormPage.css';

/**
 * Add / Edit Product page.
 *
 * Page chrome (sticky topbar with Back + title + Publish) lives here.
 * The form body is `<ProductForm layout="sections">` — same fields and
 * submit logic as the legacy sidebar, but rendered as long-scrolling
 * section cards instead of tabs. A left scroll-spy mini-nav highlights
 * the section currently in view on desktop; on mobile it collapses into
 * a horizontal pill scroller above the canvas.
 *
 * Phase B1 — design language is locked in:
 *   • brand-teal accents (never blue)
 *   • soft solid cards: 16px radius, 1px gray-100 border, subtle 2-layer
 *     shadow (no hairline-only look)
 *   • mobile-first: single column ≤1024px, sticky topbar always visible,
 *     left nav appears at ≥1024px
 *   • the form-level Save/Cancel bar is hidden — the topbar owns Publish
 */

const FORM_ID = 'product-form-page';

const PRODUCT_FORM_SECTIONS_BASE = [
  { id: 'basic',      label: 'Basics',     Icon: FileText },
  { id: 'pricing',    label: 'Pricing',    Icon: DollarSign },
  // Phase 4.A.6 — Inventory tab added in ProductForm; mirror it in
  // the side nav so the merchant can jump to Receive Stock, expiry,
  // and low-stock alert without scrolling through Pricing.
  { id: 'inventory',  label: 'Inventory',  Icon: Boxes },
  { id: 'media',      label: 'Media',      Icon: ImageIcon },
  { id: 'variations', label: 'Variations', Icon: Layers },
];

const ProductFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const toast = useToast();
  const products = useSelector((s) => s.products?.products || []);
  const productsLoaded = useSelector((s) => s.products?.status === 'succeeded');

  const isNew = !id;
  const [submitting, setSubmitting] = useState(false);
  const [activeSection, setActiveSection] = useState('basic');
  const [calcOpen, setCalcOpen] = useState(false);

  const existingProduct = useMemo(
    () => (isNew ? null : products.find((p) => (p.id || p._id) === id)),
    [isNew, products, id]
  );

  // Track the product type so we can show Digital / Appointments nav items
  // when relevant. The form owns the source of truth; we mirror it here for
  // the mini-nav. Default to the existing product's type, else 'physical'.
  const initialProductType = (existingProduct?.productType || 'physical').toLowerCase();
  const [productType, setProductType] = useState(initialProductType);

  // Mirror existing product type when it loads (edit flow).
  useEffect(() => {
    if (existingProduct?.productType) {
      setProductType(existingProduct.productType.toLowerCase());
    }
  }, [existingProduct]);

  useEffect(() => {
    if (!productsLoaded) {
      dispatch(fetchProducts());
    }
  }, [dispatch, productsLoaded]);

  // Guard: editing a product that doesn't exist in the list after load.
  useEffect(() => {
    if (!isNew && productsLoaded && !existingProduct) {
      toast.error('Product not found.');
      navigate('/products', { replace: true });
    }
  }, [isNew, productsLoaded, existingProduct, navigate, toast]);

  // Listen for product-type changes inside the form so the nav reflects
  // Digital/Appointments at the right time. The form fires a custom event
  // on its container (see ProductForm.js — non-invasive, doesn't need a
  // prop drilled in).
  const containerRef = useRef(null);
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const onTypeChange = (e) => {
      if (e.detail?.productType) setProductType(e.detail.productType);
    };
    node.addEventListener('pfs:product-type', onTypeChange);
    return () => node.removeEventListener('pfs:product-type', onTypeChange);
  }, []);

  const sections = useMemo(() => {
    const list = [...PRODUCT_FORM_SECTIONS_BASE];
    if (productType === 'digital') {
      list.splice(3, 0, { id: 'digital', label: 'Digital delivery', Icon: Download });
    }
    if (productType === 'appointment') {
      list.splice(3, 0, { id: 'appointments', label: 'Appointments', Icon: Calendar });
    }
    return list;
  }, [productType]);

  // Scroll-spy: highlight the section nearest the top of the canvas.
  useEffect(() => {
    const nodes = sections
      .map((s) => document.getElementById(`pfs-section-${s.id}`))
      .filter(Boolean);
    if (nodes.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the largest intersection ratio that's visible.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (visible[0]) {
          const id = visible[0].target.dataset.pfsSection;
          if (id) setActiveSection(id);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    nodes.forEach((n) => observer.observe(n));
    return () => observer.disconnect();
  }, [sections]);

  const handleScrollTo = useCallback((sectionId) => {
    const el = document.getElementById(`pfs-section-${sectionId}`);
    if (!el) return;
    setActiveSection(sectionId);
    // The dashboard Shell wraps page content in `.content-area`,
    // which is the actual scroll container — the window itself
    // doesn't scroll. `window.scrollTo` was a no-op here, so the
    // nav buttons appeared dead. `scrollIntoView` auto-targets the
    // nearest scrollable ancestor; the 84px sticky-topbar offset
    // is provided by `scroll-margin-top` on the section in CSS.
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  const handleSubmit = async (productData) => {
    try {
      setSubmitting(true);
      if (isNew) {
        await dispatch(createProduct({ product: productData })).unwrap();
        toast.success('Product added successfully!');
      } else {
        await dispatch(updateProduct({ id, product: productData })).unwrap();
        toast.success('Product updated successfully!');
      }
      dispatch(fetchProducts());
      navigate('/products');
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Failed to save product. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => navigate('/products');

  const pageTitle = isNew ? 'Add product' : 'Edit product';

  return (
    <Shell>
      <div className="pfs-page" ref={containerRef}>
        {/* ─── Sticky topbar ─────────────────────────────────────────── */}
        <div className="pfs-topbar">
          <div className="pfs-topbar-inner">
            <button
              type="button"
              className="pfs-back"
              onClick={handleCancel}
              aria-label="Back to products"
            >
              <ChevronLeft size={18} aria-hidden="true" />
              <span className="pfs-back-label">Products</span>
            </button>

            <div className="pfs-title">
              <span className="pfs-title-eyebrow">{isNew ? 'New product' : 'Editing'}</span>
              <span className="pfs-title-text">{existingProduct?.name || pageTitle}</span>
            </div>

            {/* Inline calculator (mobile). On desktop the corner FAB takes
                over — keeps the topbar uncluttered on wider screens. */}
            <button
              type="button"
              className="pfs-calc-inline"
              onClick={() => setCalcOpen(true)}
              aria-label="Open calculator"
              title="Calculator"
            >
              <CalcIcon size={18} aria-hidden="true" />
            </button>

            <button
              type="submit"
              form={FORM_ID}
              className="pfs-publish"
              disabled={submitting}
            >
              {submitting ? 'Saving…' : (isNew ? 'Publish' : 'Save changes')}
            </button>
          </div>
        </div>

        {/* ─── Mobile section pills (≤1023px) ────────────────────────── */}
        <nav className="pfs-pills" aria-label="Product form sections">
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              className={`pfs-pill ${activeSection === s.id ? 'active' : ''}`}
              onClick={() => handleScrollTo(s.id)}
            >
              <s.Icon size={14} aria-hidden="true" />
              <span>{s.label}</span>
            </button>
          ))}
        </nav>

        {/* ─── Two-column layout (single column on mobile) ───────────── */}
        <div className="pfs-layout">
          {/* Left scroll-spy mini-nav (desktop only) */}
          <aside className="pfs-nav" aria-label="Product form sections">
            <ul>
              {sections.map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={`pfs-nav-item ${activeSection === s.id ? 'active' : ''}`}
                    onClick={() => handleScrollTo(s.id)}
                  >
                    <s.Icon size={16} aria-hidden="true" className="pfs-nav-icon" />
                    <span>{s.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Main canvas: the existing ProductForm rendered in sections mode */}
          <main className="pfs-canvas">
            <ProductForm
              product={existingProduct}
              onSubmit={handleSubmit}
              onCancel={handleCancel}
              loading={submitting}
              layout="sections"
              formId={FORM_ID}
            />
          </main>
        </div>

        {/* Floating calculator — handy while setting prices, margins, etc. */}
        <button
          type="button"
          className="pfs-calc-fab"
          onClick={() => setCalcOpen(true)}
          aria-label="Open calculator"
          title="Calculator"
        >
          <CalcIcon size={20} aria-hidden="true" />
        </button>

        <CalculatorModal open={calcOpen} onClose={() => setCalcOpen(false)} />
      </div>
    </Shell>
  );
};

export default ProductFormPage;
